import React, { useMemo, useState } from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { Badge, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faQrcode, faGlobe } from '@fortawesome/free-solid-svg-icons';
import ms from 'milsymbol';
import parse from 'html-react-parser';
import { UnitTreeNode, InsertionIndicator } from '../../types/orbat';
import { MobileCredential } from '../../types/mobileCredentials';
import { MobileCredentialsModal } from './MobileCredentialsModal';
import { provisionMobileCredentials } from '../../store/orbatSlice';
import { useAppDispatch, useAppSelector } from '../../store/hooks';

interface UnitNodeProps {
    node: UnitTreeNode;
    eventId: string;
    isRoot?: boolean;
    /** Depth of this node in the tree; root is level 1. */
    level?: number;
    /** Active drag insertion indicator (which sibling, which edge). */
    insertion?: InsertionIndicator | null;
    onAddChild: (parentUnit: UnitTreeNode) => void;
    onDelete: (unit: UnitTreeNode) => void;
    onEdit: (unit: UnitTreeNode) => void;
}

/**
 * Draggable unit node component for the ORBAT tree.
 */
const UnitNode: React.FC<UnitNodeProps> = ({
    node,
    eventId,
    isRoot = false,
    level = 1,
    insertion = null,
    onAddChild,
    onDelete,
    onEdit,
}) => {
    const dispatch = useAppDispatch();

    // State for mobile credentials modal
    const [showMobileCredentialsModal, setShowMobileCredentialsModal] = useState(false);
    const [mobileCredential, setMobileCredential] = useState<MobileCredential | null>(null);
    const [credentialLoading, setCredentialLoading] = useState(false);
    const [credentialError, setCredentialError] = useState<string | null>(null);

    // Get user role from auth state
    const userRole = useAppSelector((state) => state.auth.user?.role);
    const isGamemasterOrAdmin = userRole === 'gamemaster' || userRole === 'admin';

    // Set up draggable
    const {
        attributes,
        listeners,
        setNodeRef: setDragRef,
        isDragging,
    } = useDraggable({
        id: node.id,
        data: {
            type: 'unit',
            node: node,
        },
    });

    // Set up droppable
    const { setNodeRef: setDropRef, isOver } = useDroppable({
        id: `drop-${node.id}`,
        data: {
            type: 'unit-drop',
            node: node,
        },
    });

    // Generate military symbol
    const symbolSvg = useMemo(() => {
        const symbol = new ms.Symbol(node.sidc, { size: 40 });
        return symbol.asSVG();
    }, [node.sidc]);

    const handleAddChild = (e: React.MouseEvent) => {
        e.stopPropagation();
        onAddChild(node);
    };

    const handleDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        onDelete(node);
    };

    const handleDoubleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onEdit(node);
    };

    const handleProvisionCredentials = async (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowMobileCredentialsModal(true);
        setCredentialLoading(true);
        setCredentialError(null);

        try {
            const result = await dispatch(
                provisionMobileCredentials({
                    eventId: eventId,
                    unitId: node.id,
                })
            ).unwrap();

            setMobileCredential(result);
        } catch (error) {
            setCredentialError(error as string);
        } finally {
            setCredentialLoading(false);
        }
    };

    const handleCloseModal = () => {
        setShowMobileCredentialsModal(false);
        setCredentialError(null);
    };

    // Combine refs for both drag and drop on the card
    const setRefs = (element: HTMLElement | null) => {
        setDragRef(element);
        setDropRef(element);
    };

    return (
        <div className="unit-node">
            {/* Vertical connector from parent (not shown for root) */}
            {!isRoot && <div className="unit-node__connector" />}

            {/* Unit card (draggable and droppable) */}
            <div
                ref={setRefs}
                className={`unit-node__card ${isDragging ? 'unit-node__card--dragging' : ''} ${
                    isOver ? 'unit-node__card--drop-target' : ''
                }`}
                onDoubleClick={handleDoubleClick}
                {...listeners}
                {...attributes}
            >
                {/* Drag insertion line — shown when a sibling is being dropped
                    before/after this unit. Level-2 siblings sit in a row, so
                    the line is vertical; deeper levels stack, so it is
                    horizontal. */}
                {insertion?.nodeId === node.id && (
                    <div
                        className={
                            `unit-node__insertion unit-node__insertion--${insertion.edge}` +
                            (level === 2 ? ' unit-node__insertion--horizontal' : '')
                        }
                    />
                )}
                <div className="unit-node__symbol">{parse(symbolSvg)}</div>
                <div className="unit-node__name" title={node.name}>
                    {node.name}
                </div>
                {(node.is_publicly_joinable ?? false) && (
                    <Badge
                        bg="info"
                        pill
                        className="unit-node__public-badge"
                        title="Claimable in the public lobby"
                        aria-label="Publicly joinable"
                    >
                        <FontAwesomeIcon icon={faGlobe} className="me-1" />
                        PUBLIC
                    </Badge>
                )}

                {/* Action buttons */}
                <div className="unit-node__actions">
                    <Button
                        variant="primary"
                        className="unit-node__action-btn"
                        onClick={handleAddChild}
                        title="Add subordinate"
                    >
                        <FontAwesomeIcon icon={faPlus} />
                    </Button>
                    {isGamemasterOrAdmin && (
                        <Button
                            variant="info"
                            className="unit-node__action-btn"
                            onClick={handleProvisionCredentials}
                            title="Generate mobile credentials"
                        >
                            <FontAwesomeIcon icon={faQrcode} />
                        </Button>
                    )}
                    {!isRoot && (
                        <Button
                            variant="danger"
                            className="unit-node__action-btn"
                            onClick={handleDelete}
                            title="Delete unit"
                        >
                            <FontAwesomeIcon icon={faTrash} />
                        </Button>
                    )}
                </div>
            </div>

            {/* Children.
                Children at level 3 and deeper stack vertically (indented list)
                instead of branching horizontally, so deep ORBATs stay readable
                without growing unboundedly wide. */}
            {node.children && node.children.length > 0 && (
                <div
                    className={`unit-node__children ${
                        level >= 2 ? 'unit-node__children--vertical' : ''
                    }`}
                >
                    {node.children
                        .slice()
                        .sort((a, b) => a.unit_order - b.unit_order)
                        .map((child) => (
                            <div key={child.id} className="unit-node__child-wrapper">
                                <UnitNode
                                    node={child}
                                    eventId={eventId}
                                    level={level + 1}
                                    insertion={insertion}
                                    onAddChild={onAddChild}
                                    onDelete={onDelete}
                                    onEdit={onEdit}
                                />
                            </div>
                        ))}
                </div>
            )}

            {/* Mobile Credentials Modal */}
            <MobileCredentialsModal
                show={showMobileCredentialsModal}
                onHide={handleCloseModal}
                credential={mobileCredential}
                loading={credentialLoading}
                error={credentialError}
                unitName={node.name}
            />
        </div>
    );
};

export default UnitNode;
